import type { ReactNode } from 'react';
import { cx, focusRing } from './cx';
import { EmptyState } from './EmptyState';
import { Skeleton } from './Skeleton';

// The register row lives in its own file now; it is re-exported from here
// so every existing `import { RegisterRow } from './DataTable'` still works.
export { RegisterRow, RowCheck, RowLeadSpace } from './RegisterRow';
export type { RegisterRowProps, RowCheckProps } from './RegisterRow';

export interface Column<Row> {
  /** Stable key, also used as the React key for cells. */
  key: string;
  header: ReactNode;
  /** Numbers right-align and set in the record face. Text does not. */
  numeric?: boolean;
  /** Hide below md. Use for columns that are context rather than the point. */
  hideOnMobile?: boolean;
  width?: string;
  render: (row: Row) => ReactNode;
  /** Label in the mobile stack. Defaults to the header. */
  cardLabel?: ReactNode;
  /** Promote to the block's title line in the mobile stack. */
  primary?: boolean;
}

export interface DataTableProps<Row> {
  columns: Array<Column<Row>>;
  rows: Row[];
  rowKey: (row: Row, index: number) => string;
  caption?: string;
  onRowClick?: (row: Row) => void;
  loading?: boolean;
  empty?: ReactNode;
  /**
   * Header sticks while the body scrolls, inside a capped scrollport.
   *
   * Off by default. It was on by default and had never worked once: the
   * `th` carried `sticky top-0` but the wrapper around the table was
   * `overflow-x-auto`, which is its own scrolling box with no vertical
   * overflow, so the header stuck to a container that never scrolled and
   * left the screen with the rest of the page. Turning it on now costs a
   * height cap, which is wrong for the six-row tables that sit inside
   * cards, so it is opt-in and only the long registers ask for it.
   */
  stickyHeader?: boolean;
  className?: string;
}

/**
 * The register. Header row in Instrument Sans 600 12.5px uppercase on a line-strong
 * rule; body rows 44px tall, hairline-ruled, numeric columns right-aligned in
 * JetBrains Mono; hover is a green tint and nothing moves. Below md the same rows
 * stack as ruled blocks of label/value pairs, because a grants officer
 * checking a tranche on the way to a meeting will not scroll sideways.
 */
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  caption,
  onRowClick,
  loading,
  empty,
  stickyHeader = false,
  className,
}: DataTableProps<Row>) {
  if (loading) {
    return (
      <div role="status" aria-busy="true" aria-live="polite" className={cx('py-3', className)}>
        <span className="sr-only">Loading</span>
        <Skeleton lines={5} />
      </div>
    );
  }

  if (rows.length === 0) {
    return <>{empty ?? <EmptyState size="sm" title="Nothing on the register yet" />}</>;
  }

  const primary = columns.find((c) => c.primary) ?? columns[0];

  return (
    <div className={className}>
      {/* Register for md and up, and on paper: A4 is narrower than the md
          breakpoint, so print: has to opt back in or a board pack prints the
          phone stack below instead of the table. */}
      <div
        className={cx(
          'hidden md:block print:block',
          // A real scrollport, so `sticky` has something to stick to. Never
          // on paper: a capped, scrolling box prints as one screenful and
          // the rest of the register is simply missing from the board pack.
          stickyHeader
            ? 'overflow-auto max-h-[calc(100vh-14rem)] print:max-h-none print:overflow-visible'
            : 'overflow-x-auto',
        )}
      >
        <table className="w-full border-collapse text-sm">
          {caption ? <caption className="sr-only">{caption}</caption> : null}
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  style={c.width ? { width: c.width } : undefined}
                  className={cx(
                    'border-b border-line-strong bg-paper px-3 py-2 text-xs font-semibold uppercase tracking-[0.06em] text-ink-2',
                    c.numeric ? 'text-right' : 'text-left',
                    stickyHeader && 'sticky top-0 z-10',
                  )}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={rowKey(row, i)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cx(
                  'h-tap border-b border-line transition-colors duration-state ease-ct',
                  onRowClick && 'cursor-pointer hover:bg-forest-whisper active:bg-forest-faint',
                )}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={cx('px-3 py-2 align-middle text-ink', c.numeric && 'record text-right')}
                  >
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Ruled blocks below md. Same data, no sideways scroll. Never on
          paper: the table above is the printed form. */}
      <ul className="flex flex-col border-t border-line-strong md:hidden print:hidden">
        {rows.map((row, i) => (
          <li key={rowKey(row, i)} className="border-b border-line">
            <div
              role={onRowClick ? 'button' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
              className={cx('py-3', onRowClick && cx('cursor-pointer hover:bg-forest-whisper active:bg-forest-faint', focusRing))}
            >
              <p className="mb-2 text-sm font-semibold text-ink">{primary.render(row)}</p>
              <dl className="flex flex-col gap-1">
                {columns
                  .filter((c) => c.key !== primary.key && !c.hideOnMobile)
                  .map((c) => (
                    <div key={c.key} className="flex items-baseline justify-between gap-3">
                      <dt className="text-xs text-ink-3">{c.cardLabel ?? c.header}</dt>
                      <dd className={cx('text-xs text-ink', c.numeric && 'record')}>{c.render(row)}</dd>
                    </div>
                  ))}
              </dl>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

